import { useEffect, useState } from 'react';
import { Alert, Progress, Radio, Button, Spin, Checkbox } from 'antd';
import Container from '../../components/Layout/Container';
import common from './Common.module.scss';
import local from './QuestionSection.module.scss';
import { fetchSurveyConfig } from '../../utils/fetchSurveyConfig';
import { useSurveyContext } from '../../context/SurveyContext';
import { SUBMIT_ERROR_MESSAGE, useSurveySubmit } from './useSurveySubmit';

const QuestionSection = () => {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<any>({});
  const {
    surveyState,
    setCurrentStep,
    setCurrentSubsection,
    addAnswers,
    setMidBreakSeen
  } = useSurveyContext();
  const { submit, isSubmitting, error: submitError, setError: setSubmitError } = useSurveySubmit();
  
  // Get current section and subsection from context
  const { currentSection: sectionIdx, currentSubsection: subsectionIdx } = surveyState;
  
  // Check if we've seen the mid-break for this section
  const hasSeenMidBreak = sectionIdx === 0 ? surveyState.hasSeenMidBreak1 : surveyState.hasSeenMidBreak2;
  
  // Configuration loading for the survey
  useEffect(() => {
    fetchSurveyConfig().then(cfg => {
      setConfig(cfg);
      setLoading(false);
    });
  }, []);

  // Restore answers from survey context when returning from breaks
  useEffect(() => {
    if (config && surveyState.answers && Object.keys(surveyState.answers).length > 0) {
      setAnswers(surveyState.answers);
    }
  }, [config, surveyState.answers]);

  if (loading || !config) {
    return <div className={common.surveyWrapper}><Spin size="large" /></div>;
  }

  const section = config.sections[sectionIdx];
  
  // Get current and next subsection for display (2 subsections at a time)
  const currentSubsection = section.subsections[subsectionIdx];
  const nextSubsection = section.subsections[subsectionIdx + 1];
  const displaySubsections = [currentSubsection, nextSubsection].filter(Boolean);

  const totalSubsectionsInSurvey = config.sections.reduce((total: number, s: any) => total + s.subsections.length, 0);
  const completedSubsections = config.sections
    .slice(0, sectionIdx)
    .reduce((total: number, s: any) => total + s.subsections.length, 0) + subsectionIdx;

  const progress = totalSubsectionsInSurvey > 0
    ? Math.round((completedSubsections / totalSubsectionsInSurvey) * 100)
    : 0;

  // Record metrics for Section 3 (career assessment)
  const isCareerSection = section.id === 'sec_career_assessment';

  const getScalarValue = (stored: any) => {
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
      return stored.value;
    }
    return stored;
  };

  const getSelectedIdFromStored = (stored: any, question: any): any => {
    if (!stored) return undefined;

    // Section 3 value: { id, metric }
    if (typeof stored === 'object' && !Array.isArray(stored)) {
      return stored.id ?? stored.value;
    }

    // Section 1 and 2 value: could be id or metric
    const byId = (question.answer_options || []).find((o: any) => (o.id ?? o.text) === stored);
    if (byId) return byId.id ?? byId.text;
    const byMetric = (question.answer_options || []).find((o: any) => o.metric === stored);
    if (byMetric) return byMetric.id ?? byMetric.text;

    return stored;
  };

  const getSelectedIdsFromStored = (stored: any, question: any): any[] => {
    if (!stored) return [];

    if (Array.isArray(stored)) {
      if (stored.length > 0 && typeof stored[0] === 'object') {
        // Section 3 value: [{ id, metric }]
        return stored.map((item: any) => item.id ?? item.value).filter(Boolean);
      }
      // Section 1 and 2 value: array of strings (ids or metrics)
      return stored.map((val: any) => {
        const byId = (question.answer_options || []).find((o: any) => (o.id ?? o.text) === val);
        if (byId) return byId.id ?? byId.text;
        const byMetric = (question.answer_options || []).find((o: any) => o.metric === stored);
        return byMetric ? (byMetric.id ?? byMetric.text) : val;
      }).filter(Boolean);
    }

    return [];
  };

  const handleLocalAnswer = (subsectionId: string, question: any, rawValue: any) => {
    let valueToStore: any = rawValue;

    if (isCareerSection) {
      if (question.type === 'multiple_select') {
        // Store objects to preserve which option among duplicate metrics was chosen
        const selectedIds: any[] = Array.isArray(rawValue) ? rawValue : [];
        valueToStore = selectedIds.map((id: any) => {
          const opt = (question.answer_options || []).find((o: any) => (o.id ?? o.text) === id);
          return { id, metric: opt?.metric ?? null };
        });
      } else if (question.type === 'single_select') {
        const id = rawValue;
        const opt = (question.answer_options || []).find((o: any) => (o.id ?? o.text) === id);
        valueToStore = { id, metric: opt?.metric ?? null };
      } else {
        // Likert/radio style: store numeric value and question metric
        valueToStore = { value: rawValue, metric: question.metric ?? null };
      }
    }

    setAnswers((prev: any) => ({
      ...prev,
      [section.id]: {
        ...prev[section.id],
        [subsectionId]: {
          ...prev[section.id]?.[subsectionId],
          [question.id]: valueToStore,
        },
      },
    }));
  };

  // Check if all questions in both subsections are answered
  const allAnswered = displaySubsections.every((subsec: any) => 
    subsec.questions.every((q: any) => {
      const answer = answers?.[section.id]?.[subsec.id]?.[q.id];
      if (q.type === 'multiple_select') {
        return Array.isArray(answer) && answer.length > 0;
      }
      return answer !== undefined && answer !== null && answer !== '';
    })
  );

  const handleNext = async () => {
    // Calculate next subsection index (advance by 2)
    const nextSubsectionIdx = subsectionIdx + 2;
    const isLastSubsection = nextSubsectionIdx >= section.subsections.length;
    const isLastSection = sectionIdx === config.sections.length - 1;

    // Check if we should show the mid-section break for section 1
    const shouldShowMidBreak = sectionIdx === 0 && subsectionIdx === 4 && !hasSeenMidBreak; // Changed from 5 to 4 since we advance by 2
    
    // Check if we should show the mid-section break for section 2
    const shouldShowSection2MidBreak = sectionIdx === 1 && subsectionIdx === 4 && !hasSeenMidBreak; // Changed from 5 to 4 since we advance by 2

    // Helper function to save answers and then proceed
    const saveAnswersAndProceed = async (nextStep: string) => {
      await addAnswers(answers);
      // Use requestAnimationFrame to ensure state is updated
      requestAnimationFrame(() => {
        setCurrentStep(nextStep);
      });
    };

    // If the user is on the last subsection and the last section, submit the survey
    if (isLastSubsection && isLastSection) {
      try {
        // Get answers from form DOM elements to ensure we capture everything for both subsections
        const completeAnswers = { ...answers };
        
        displaySubsections.forEach((subsec: any) => {
          const currentSubsectionAnswers: any = {};
          subsec.questions.forEach((q: any) => {
            // Try to get answer from local state first
            let currentValue = answers?.[section.id]?.[subsec.id]?.[q.id];
            
            // If no value in local state, try to get from form DOM
            if (currentValue === undefined || currentValue === null || currentValue === '') {
              const questionElement = document.querySelector(`[data-question-id="${q.id}"]`);
              if (questionElement) {
                if (q.type === 'multiple_select') {
                  // For checkboxes, get all checked values
                  const checkedBoxes = questionElement.querySelectorAll('input[type="checkbox"]:checked');
                  currentValue = Array.from(checkedBoxes).map((box: Element) => (box as HTMLInputElement).value);
                } else if (q.type === 'single_select' || q.type === 'radio') {
                  // For radio buttons, get selected value
                  const selectedRadio = questionElement.querySelector('input[type="radio"]:checked');
                  currentValue = selectedRadio ? (selectedRadio as HTMLInputElement).value : undefined;
                }
              }
            }
            
            // Store the answer if we found one
            if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
              currentSubsectionAnswers[q.id] = currentValue;
            } else {
              console.warn(`⚠️ No answer found for question ${q.id}`);
            }
          });

          // Validate that we have answers for all questions in this subsection
          const missingAnswers = subsec.questions.filter((q: any) => !currentSubsectionAnswers[q.id]);
          if (missingAnswers.length > 0) {
            console.error('❌ Missing answers for questions:', missingAnswers.map((q: any) => q.id));
            throw new Error(`Missing answers for ${missingAnswers.length} questions in ${subsec.id}`);
          }

          // Add this subsection's answers to the complete answers
          completeAnswers[section.id] = {
            ...completeAnswers[section.id],
            [subsec.id]: {
              ...completeAnswers[section.id]?.[subsec.id],
              ...currentSubsectionAnswers,
            },
          };
        });

        // Save answers to context first
        await addAnswers(completeAnswers);

        // Now submit the survey with the updated state
        await submit(completeAnswers);
      } catch (error) {
        console.error('❌ QuestionSection: Error during survey submission:', error);
        setSubmitError(SUBMIT_ERROR_MESSAGE);
      }
    } else if (shouldShowMidBreak) {
      // Save current answers to context before moving to mid-section break
      setMidBreakSeen(0); // Mark section 1 mid-break as seen
      await saveAnswersAndProceed('break-1-mid');
    } else if (shouldShowSection2MidBreak) {
      // Save current answers to context before moving to section 2 mid-section break
      setMidBreakSeen(1); // Mark section 2 mid-break as seen
      await saveAnswersAndProceed('break-2-mid');
    } else if (isLastSubsection) {
      // Save current answers to context before moving to break page
      if (sectionIdx === 0) {
        // Moving from section 1 to break 1
        await saveAnswersAndProceed('break-1');
      } else if (sectionIdx === 1) {
        // Moving from section 2 to break 2
        await saveAnswersAndProceed('break-2');
      }
      // Note: Section 3 (sectionIdx === 2) is handled in the first if block above
      // when both isLastSubsection AND isLastSection are true
    } else {
      // Checkpoint before advancing, so a refresh mid-section doesn't leave the
      // user past screens whose answers were never saved.
      await addAnswers(answers);
      // Move to next subsection within the same section (advance by 2)
      setCurrentSubsection(nextSubsectionIdx);
    }
  };

  return (
    <div className={`${common.surveyWrapper} ${local.surveyWrapper}`}>
      <Container>
        <div className={common.surveyTextSection}>
          <div className={local.surveyProgressBar}>
            <Progress percent={progress} showInfo={false} className={local.surveyProgressBarTrack} />
            <span className={local.surveyProgressPercent}>{progress}%</span>
          </div>
          <div className={`${common.surveyFormWrapper} ${local.surveyFormWrapper}`}>
            {displaySubsections.map((subsec: any) => (
              <div key={subsec.id}>
                {subsec.questions.map((q: any) => (
                  <div key={`${subsec.id}-${q.id}`} className={local.surveyQuestionBlock} data-question-id={q.id}>
                    <div className={local.surveyQuestionText}>{q.text}</div>
                    {(!q.type || q.type === 'radio') && (
                      <Radio.Group
                        value={getScalarValue(answers?.[section.id]?.[subsec.id]?.[q.id])}
                        onChange={e => handleLocalAnswer(subsec.id, q, e.target.value)}
                        className={local.surveyRadioGroup}
                      >
                        {(q.answer_options || config.answer_options).map((opt: any, idx: number) => (
                          <Radio key={opt.value || idx} value={opt.value || idx + 1} className={local.surveyRadioButton}>
                            {opt.text}
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                    {q.type === 'multiple_select' && (
                      <Checkbox.Group
                        value={getSelectedIdsFromStored(answers?.[section.id]?.[subsec.id]?.[q.id], q)}
                        onChange={checkedValues => handleLocalAnswer(subsec.id, q, checkedValues)}
                        className={local.surveyCheckboxGroup}
                      >
                        {q.answer_options.map((opt: any) => (
                          <Checkbox
                            key={opt.id || opt.text}
                            value={opt.id || opt.text}
                            className={local.surveyRadioButton}
                          >
                            {opt.text}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                    )}
                    {q.type === 'single_select' && (
                      <Radio.Group
                        value={getSelectedIdFromStored(answers?.[section.id]?.[subsec.id]?.[q.id], q)}
                        onChange={e => handleLocalAnswer(subsec.id, q, e.target.value)}
                        className={local.surveyRadioGroup}
                      >
                        {q.answer_options.map((opt: any) => (
                          <Radio
                            key={opt.id || opt.text}
                            value={opt.id || opt.text}
                            className={local.surveyRadioButton}
                          >
                            {opt.text}
                          </Radio>
                        ))}
                      </Radio.Group>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {submitError && (
            <div className={local.surveySubmitError}>
              <Alert type="error" message={submitError} showIcon />
            </div>
          )}
          <div className={common.surveyButtonWrapper}>
              <Button
                type="primary"
                className={common.surveyButton}
                onClick={handleNext}
                disabled={!allAnswered}
                loading={isSubmitting}
                block
              >
                Next
              </Button>
            </div>
        </div>
      </Container>
    </div>
  );
};

export default QuestionSection;